import { useCallback, useEffect, useMemo, useState } from 'react'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery as useAppQuery } from 'modules/core/hooks/useQuery'
import { useProductCatalog } from 'modules/it_management/lib/graphqlProductCatalogContext'
import type {
  MarketplaceCart,
  MarketplaceCartLine,
} from 'modules/it_management/lib/marketplaceCartGraphql'
import {
  ADD_MARKETPLACE_CART_LINE,
  CREATE_MARKETPLACE_CART,
  GET_MARKETPLACE_CART,
  REMOVE_MARKETPLACE_CART_LINE,
  UPDATE_MARKETPLACE_CART_LINE,
} from 'modules/it_management/lib/marketplaceCartGraphql'
import { getMarketplaceGraphqlClient } from 'modules/it_management/lib/marketplaceGraphqlClient'
import { PRODUCT_CATALOG_VARIANTS_DOCUMENT } from 'modules/it_management/lib/productCatalogGraphql'

import { DEVICE_CATALOG_MAX_QUANTITY } from './constants'
import type { CartItem, OfferType } from './types'
import { CART_STORAGE_KEY } from './types'

const MARKETPLACE_CART_ID_KEY = 'device_catalog_marketplace_cart_id'

function getCartIdStorageKey(companyId: string, userId: string): string {
  return `${MARKETPLACE_CART_ID_KEY}_${companyId}_${userId}`
}

function getCachedCartId(storageKey: string): string | null {
  if (typeof sessionStorage === 'undefined') return null
  const cached = sessionStorage.getItem(storageKey)
  return cached && cached.trim() !== '' ? cached : null
}

function setCachedCartId(storageKey: string, id: string | null): void {
  if (typeof sessionStorage === 'undefined') return
  if (id) {
    sessionStorage.setItem(storageKey, id)
  } else {
    sessionStorage.removeItem(storageKey)
  }
}

function isCartPayload(data: unknown): data is {
  addCartLine?: MarketplaceCart
  updateCartLine?: MarketplaceCart
  removeCartLine?: MarketplaceCart
  createCart?: MarketplaceCart
} {
  return (
    typeof data === 'object' &&
    data !== null &&
    ('addCartLine' in data ||
      'updateCartLine' in data ||
      'removeCartLine' in data ||
      'createCart' in data)
  )
}

function getCartFromMutationData(data: unknown): MarketplaceCart | null {
  if (!isCartPayload(data)) return null
  return data.addCartLine ?? data.updateCartLine ?? data.removeCartLine ?? data.createCart ?? null
}

function isMarketplaceCart(x: unknown): x is MarketplaceCart {
  return (
    typeof x === 'object' &&
    x !== null &&
    'id' in x &&
    'companyId' in x &&
    'customerId' in x &&
    'cartLines' in x
  )
}

function getCartFromCreateData(data: unknown): MarketplaceCart | null {
  if (
    typeof data === 'object' &&
    data !== null &&
    'createCart' in data &&
    data.createCart != null &&
    isMarketplaceCart(data.createCart)
  ) {
    return data.createCart
  }
  return null
}

type VariantOfferForCart = {
  id: string
  type: string
  currency: string
  cost?: number | null
  period?: number | null
}

type VariantTaxForCart = {
  name: string
  value: number
}

type VariantDetailsForCart = {
  name: string
  image: string
  offers: VariantOfferForCart[]
  productTaxes: VariantTaxForCart[]
}

const IVA_RATE = 0.21

function isCanonTaxName(name: string): boolean {
  const normalized = name.trim().toLowerCase()
  return (
    normalized.includes('canon') ||
    normalized.includes('private_copy_levy') ||
    normalized.includes('levy')
  )
}

function mapApiOfferTypeToOfferType(apiType: string): OfferType {
  if (apiType === 'renting' || apiType === 'credit') return apiType
  return 'onetime'
}

function cartLinesToItems(
  lines: MarketplaceCartLine[],
  variantDetails: Map<string, VariantDetailsForCart>
): CartItem[] {
  return lines.map((line) => {
    const details = variantDetails.get(line.productVariantId)
    const offer = details?.offers?.find((o) => o.id === line.productOfferId) ?? null
    const offerType = offer ? mapApiOfferTypeToOfferType(offer.type) : 'onetime'
    const offerPeriod = offer?.period != null && offerType !== 'onetime' ? offer.period : null

    return {
      variantId: line.productVariantId,
      name: details?.name ?? line.productVariantId,
      price: parseFloat(line.unitPrice),
      currency: offer?.currency ?? 'EUR',
      image: details?.image ?? '',
      quantity: line.quantity,
      addons: [],
      offerId: line.productOfferId,
      offerType,
      offerPeriod,
    }
  })
}

const CART_UPDATED_EVENT = 'device-catalog-cart-updated'

export function useDeviceCatalogCart() {
  const { company, employee } = useCurrentSession()
  const companyId = String(company.id)
  const userId = String(employee.id)
  const storageKey = getCartIdStorageKey(companyId, userId)

  const [cartId, setCartIdState] = useState<string | null>(() => getCachedCartId(storageKey))
  const [cart, setCart] = useState<MarketplaceCart | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [cartError, setCartError] = useState<string | null>(null)

  useEffect(() => {
    setCartIdState(getCachedCartId(storageKey))
    setCart(null)
  }, [storageKey])

  const marketplaceClient = getMarketplaceGraphqlClient()
  const { queryContext, buildProductImageUrl } = useProductCatalog()

  // Sync cart lines to localStorage whenever cart updates, so header badge reflects current count
  useEffect(() => {
    if (!cart?.cartLines) {
      try {
        localStorage.removeItem(CART_STORAGE_KEY)
      } catch {
        // Ignore storage errors
      }
      return
    }
    try {
      const cartDataForStorage = cart.cartLines.map((line) => ({
        id: line.id,
        productVariantId: line.productVariantId,
        productOfferId: line.productOfferId,
        quantity: line.quantity,
      }))
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartDataForStorage))
    } catch {
      // Ignore storage errors
    }
  }, [cart?.cartLines])

  const fetchCart = useCallback(
    (id: string) => {
      marketplaceClient
        .query({
          query: GET_MARKETPLACE_CART,
          variables: { id },
          fetchPolicy: 'network-only',
        })
        .then((res: { data?: { cart?: MarketplaceCart | null } }) => {
          const fetched = res.data?.cart ?? null
          if (!fetched) {
            setCachedCartId(storageKey, null)
            setCartIdState(null)
            window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT))
          }
          setCart(fetched)
        })
        .catch(() => {
          setCart(null)
        })
    },
    [marketplaceClient, storageKey]
  )

  useEffect(() => {
    if (!cartId) return
    fetchCart(cartId)
  }, [cartId, fetchCart])

  // Re-sync cart when another provider instance makes changes (e.g. activity popup adds items)
  useEffect(() => {
    const handleCartUpdated = () => {
      const latestId = getCachedCartId(storageKey)
      if (!latestId) return
      if (latestId !== cartId) {
        setCartIdState(latestId)
      } else {
        fetchCart(latestId)
      }
    }
    window.addEventListener(CART_UPDATED_EVENT, handleCartUpdated)
    return () => window.removeEventListener(CART_UPDATED_EVENT, handleCartUpdated)
  }, [cartId, fetchCart, storageKey])

  const variantIds = useMemo(
    () => cart?.cartLines?.map((l: MarketplaceCartLine) => l.productVariantId) ?? [],
    [cart?.cartLines]
  )

  const { data: variantsData } = useAppQuery(PRODUCT_CATALOG_VARIANTS_DOCUMENT, {
    variables: { ids: variantIds },
    skip: variantIds.length === 0,
    context: queryContext,
  })

  const variantDetailsMap = useMemo(() => {
    const map = new Map<string, VariantDetailsForCart>()
    const nodes = variantsData?.itManagement?.distributorProductVariantsConnection?.nodes ?? []
    for (const node of nodes) {
      if (!node?.id) continue
      const name = node.product?.name ?? node.sku ?? node.id
      const imageUrl = node.firstProductMediaImageUrl ?? undefined
      const offers: VariantOfferForCart[] = (node.offers ?? []).map((o) => ({
        id: o.id,
        type: o.type,
        currency: o.currency,
        cost: o.cost ?? null,
        period: o.period ?? null,
      }))
      map.set(node.id, {
        name,
        image: buildProductImageUrl(imageUrl) ?? '',
        offers,
        productTaxes: (node.productTaxes ?? []).map((tax) => ({
          name: tax.taxName,
          value: tax.taxValue,
        })),
      })
    }
    return map
  }, [variantsData, buildProductImageUrl])

  const items: CartItem[] = useMemo(
    () => (cart ? cartLinesToItems(cart.cartLines, variantDetailsMap) : []),
    [cart, variantDetailsMap]
  )

  const setCartId = useCallback(
    (id: string | null) => {
      setCartIdState(id)
      setCachedCartId(storageKey, id)
      window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT))
    },
    [storageKey]
  )

  const ensureCart = useCallback(async (): Promise<string> => {
    const cached = getCachedCartId(storageKey)
    if (cached) {
      setCartIdState(cached)
      return cached
    }
    const result = await marketplaceClient.mutate({
      mutation: CREATE_MARKETPLACE_CART,
      variables: {},
    })
    const newCart = getCartFromCreateData(result.data)
    if (!newCart?.id) {
      throw new Error(result.errors?.[0]?.message ?? 'Failed to create cart')
    }
    setCartId(newCart.id)
    setCart(newCart)
    return newCart.id
  }, [marketplaceClient, setCartId, storageKey])

  const addItem = useCallback(
    async (item: CartItem) => {
      setCartError(null)
      try {
        const cid = await ensureCart()
        const line = cart?.cartLines?.find(
          (l: MarketplaceCartLine) =>
            l.productVariantId === item.variantId && l.productOfferId === item.offerId
        )
        const quantity = Math.max(1, Math.min(DEVICE_CATALOG_MAX_QUANTITY, item.quantity || 1))

        if (line) {
          const result = await marketplaceClient.mutate({
            mutation: UPDATE_MARKETPLACE_CART_LINE,
            variables: {
              cartLineId: line.id,
              quantity: Math.min(DEVICE_CATALOG_MAX_QUANTITY, line.quantity + quantity),
            },
          })
          const updated = getCartFromMutationData(result.data)
          if (updated) {
            setCart(updated)
            window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT))
          }
        } else {
          const result = await marketplaceClient.mutate({
            mutation: ADD_MARKETPLACE_CART_LINE,
            variables: {
              cartId: cid,
              productId: item.productId ?? item.variantId,
              productVariantId: item.variantId,
              productOfferId: item.offerId,
              quantity,
              unitPrice: item.price,
            },
          })
          const updated = getCartFromMutationData(result.data)
          if (updated) {
            setCart(updated)
            window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT))
          }
        }
        setIsOpen(true)
      } catch (err) {
        setCartError(err instanceof Error ? err.message : 'Failed to add to cart')
      }
    },
    [cart, ensureCart, marketplaceClient]
  )

  const removeItem = useCallback(
    async (variantId: string, offerId: string) => {
      setCartError(null)
      const line = cart?.cartLines?.find(
        (l: MarketplaceCartLine) => l.productVariantId === variantId && l.productOfferId === offerId
      )
      if (!line) return
      try {
        const result = await marketplaceClient.mutate({
          mutation: REMOVE_MARKETPLACE_CART_LINE,
          variables: { cartLineId: line.id },
        })
        const updated = getCartFromMutationData(result.data)
        if (updated) {
          setCart(updated)
          window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT))
        }
      } catch (err) {
        setCartError(err instanceof Error ? err.message : 'Failed to remove from cart')
      }
    },
    [cart?.cartLines, marketplaceClient]
  )

  const updateQuantity = useCallback(
    async (variantId: string, offerId: string, quantity: number) => {
      setCartError(null)
      const clamped = Math.max(0, Math.min(DEVICE_CATALOG_MAX_QUANTITY, quantity))
      const line = cart?.cartLines?.find(
        (l: MarketplaceCartLine) => l.productVariantId === variantId && l.productOfferId === offerId
      )
      if (!line) return
      if (clamped === 0) {
        await removeItem(variantId, offerId)
        return
      }
      try {
        const result = await marketplaceClient.mutate({
          mutation: UPDATE_MARKETPLACE_CART_LINE,
          variables: {
            cartLineId: line.id,
            quantity: clamped,
          },
        })
        const updated = getCartFromMutationData(result.data)
        if (updated) {
          setCart(updated)
          window.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT))
        }
      } catch (err) {
        setCartError(err instanceof Error ? err.message : 'Failed to update quantity')
      }
    },
    [cart?.cartLines, marketplaceClient, removeItem]
  )

  const clearCart = useCallback(() => {
    setCart(null)
    setCartId(null)
    setCartError(null)
  }, [setCartId])

  const subtotal = useMemo(
    () => items.reduce((sum, item) => sum + item.price * item.quantity, 0),
    [items]
  )
  const { vatTotal, canonTotal } = useMemo(() => {
    let canonAmount = 0

    for (const item of items) {
      const details = variantDetailsMap.get(item.variantId)
      if (!details) continue

      for (const tax of details.productTaxes) {
        const taxTotalForLine = tax.value * item.quantity
        if (isCanonTaxName(tax.name)) {
          canonAmount += taxTotalForLine
        }
      }
    }

    return {
      vatTotal: subtotal * IVA_RATE,
      canonTotal: canonAmount,
    }
  }, [items, subtotal, variantDetailsMap])
  const itemCount = useMemo(() => items.reduce((sum, item) => sum + item.quantity, 0), [items])
  const total = subtotal

  return {
    items,
    isOpen,
    itemCount,
    subtotal,
    vatTotal,
    canonTotal,
    total,
    cartId: cart?.id ?? null,
    addItem,
    removeItem,
    updateQuantity,
    clearCart,
    openCart: useCallback(() => setIsOpen(true), []),
    closeCart: useCallback(() => setIsOpen(false), []),
    cartError,
  }
}
